import { IoLocationSharp } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import PageLayout from "../components/global/PageLayout";
import { useNavigate, useParams, Link } from "react-router-dom";
import loader from "../methods/loader";
import ApiClient from "../methods/api/apiClient";
import datepipeModel from "../models/datepipemodel";
import { Tooltip } from "antd";
import { BsCheck, BsCrosshair } from "react-icons/bs";
import Modal from "../components/common/Modal";
import AddAttendee from "./Events/AddAttendee";
import { toast } from "react-toastify";
import Members from "../components/Events/Member";
import rolesModel from "../models/roles.model";
import { FiPrinter, FiUser } from "react-icons/fi";
import { MdInsertLink, MdOutlineShare } from "react-icons/md";
import Groups from "./Groups";
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { useSelector } from "react-redux";
import MemberHistory from "./Events/MemberHistory";
import RequestGroup from "./Events/RequestGroup";
import { LiaUserClockSolid } from "react-icons/lia";
import Swal from 'sweetalert2';
import { decryptId, encryptId } from "../components/common/Encryption/encryption";
import { RiRestTimeLine } from "react-icons/ri";
import { FaHandshake } from 'react-icons/fa';
import environment from "../environment";
import React, { useEffect, useState } from "react";
import ThankYouManager from './Events/ThankYouManager';

const ManagerRequestForm = ({ attendee, allAttendees, eventId, groupId, user, onComplete, connectMeetLabel }: any) => {
  const [selectedOption, setSelectedOption] = useState(attendee?.connectMeet || "system_generate");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [acList, setAcList] = useState<any[]>([]); // attendee's Already Connected list

  // Fetch existing request for this attendee
  useEffect(() => {
    const fetchExistingRequest = async () => {
      try {
        const email = attendee?.memberDetails?.email || attendee?.email;
        if (email && eventId) {
          const res = await ApiClient.get("api/group-request/list", {
            eventId: eventId,
            requestedBy: email
          });
          
          if (res?.success && res?.data?.length > 0) {
            const existingRequest = res.data[0];
            setSelectedOption(existingRequest.requestType || "system_generate");
            
            if (existingRequest.attendeesDetails && existingRequest.attendeesDetails.length > 0) {
              const existingIds = existingRequest.attendeesDetails
                .filter((a: any) => a?._id)
                .map((a: any) => a._id.toString());
              setSelectedMembers(existingIds);
            }
          } else {
            // cw: mark default to Available in the Manage ${connectMeetLabel} Request button
            setSelectedOption("system_generate");
          }
        }
      } catch (err) {
        console.error("Failed to fetch existing request:", err);
        // cw: On error, default to Available
        setSelectedOption("system_generate");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchExistingRequest();
  }, [attendee, eventId]);

  // cw: Fetch attendee's Already Connected list
  useEffect(() => {
    const userId = attendee?.userId || attendee?.memberDetails?.userId || attendee?.memberDetails?._id;
    if (userId && groupId) {
      ApiClient.get(`api/exclusions/list?groupId=${groupId}&userId=${userId}`).then((res: any) => {
        if (res?.success && res?.data) {
          setAcList(res.data);
        }
      }).catch(() => {});
    }
  }, [attendee, groupId]);

  // cw: Check if a member is on the attendee's AC list (by userId)
  const isOnAcList = (userId: string) => {
    if (!userId) return false;
    return acList.some((excl: any) =>
      excl.excludedUserId?._id?.toString() === userId?.toString() ||
      excl.excludedUserId?.toString() === userId?.toString()
    );
  };

  // cw: Get the AC exclusion record for a member (by userId)
  const getAcExclusion = (userId: string) => {
    if (!userId) return null;
    return acList.find((excl: any) =>
      excl.excludedUserId?._id?.toString() === userId?.toString() ||
      excl.excludedUserId?.toString() === userId?.toString()
    );
  };

  // cw: Handle checkbox toggle with AC list check
  const handleMemberToggle = (memberId: string, memberName: string, checked: boolean, memberUserId?: string) => {
    if (checked && memberUserId && isOnAcList(memberUserId)) {
      const excl = getAcExclusion(memberUserId);
      const userId = attendee?.userId || attendee?.memberDetails?.userId || attendee?.memberDetails?._id;
      Swal.fire({
        title: 'Already Connected',
        text: `${attendee?.memberDetails?.fullName || attendee?.fullName || 'This attendee'} has ${memberName} on their Already Connected list.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove From AC',
        cancelButtonText: 'Cancel Request',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed && excl) {
          // Remove from AC list
          ApiClient.put('api/exclusions/remove', { id: excl._id, userId }).then((res: any) => {
            if (res?.success) {
              setAcList(acList.filter((e: any) => e._id !== excl._id));
              setSelectedMembers([...selectedMembers, memberId]);
              toast.success('Removed from Already Connected list');
            } else {
              toast.error(res?.message || 'Failed to remove from AC list');
            }
          }).catch(() => toast.error('Failed to remove from AC list'));
        }
        // If cancelled, don't add the member
      });
    } else if (checked) {
      setSelectedMembers([...selectedMembers, memberId]);
    } else {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    }
  };

  const availableMembers = allAttendees
    .filter((itm: any) => {
      const email = itm?.memberDetails?.email || itm?.email;
      const attendeeEmail = attendee?.memberDetails?.email || attendee?.email;
      return email !== attendeeEmail;
    })
    .map((itm: any) => ({
      id: itm.id || itm._id,
      name: itm?.memberDetails?.fullName || itm?.fullName || 'Guest',
      email: itm?.memberDetails?.email || itm?.email,
      userId: itm?.memberDetails?._id || itm?.userId
    }))
    .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

  const handleSubmit = () => {
    if (selectedOption === "connect_with_members" && selectedMembers.length === 0) {
      toast.error("Please select at least one member");
      return;
    }
  
    setIsSubmitting(true);
    
    const email = attendee?.memberDetails?.email || attendee?.email;
    
    // cw: Fetch the actual attendee record to get the userId
    ApiClient.get("api/attendees/list", { 
      eventId: eventId, 
      email: email 
    }).then(attendeeRes => {
      let odlUserId = null;
      
      if (attendeeRes?.success && attendeeRes?.data?.length > 0) {
        const actualAttendee = attendeeRes.data[0];
        odlUserId = actualAttendee.userId;
        console.log("Found userId from DB:", odlUserId);
      }
      
      // Fallback: try to get userId from attendee prop
      if (!odlUserId) {
        if (attendee?.userId) {
          odlUserId = attendee.userId;
        } else if (attendee?.memberDetails?.userId) {
          odlUserId = attendee.memberDetails.userId;
        }
        // Guests won't have userId - that's okay, backend will handle by email
      }
      
      const payload: any = {
        eventId: eventId,
        groupId: groupId,
        requestedBy: email,
        attendeeIds: selectedMembers,
        requestType: selectedOption,
        managedBy: user?.email,
        attendeeId: attendee?._id || attendee?.id  // Always include attendeeId
      };
      
      // Only include userId if it exists (for registered users)
      if (odlUserId) {
        payload.userId = odlUserId;
      }

      console.log("Submitting payload:", payload);
  
      // Update group request
      ApiClient.post("api/group/request", payload).then(res => {
        ApiClient.put("api/attendees/update", {
          id: attendee?.id || attendee?._id,
          connectMeet: selectedOption
        }).then(() => {
          setIsSubmitting(false);
          if (res) {
            toast.success(res.message || `${connectMeetLabel} request updated`);
            onComplete();
          }
        }).catch(err => {
          setIsSubmitting(false);
          console.error("Failed to update attendee:", err);
          toast.error("Failed to update attendee status");
        });
      }).catch(err => {
        setIsSubmitting(false);
        console.error("Failed to update request:", err);
        toast.error("Failed to update request");
      });
    }).catch(err => {
      setIsSubmitting(false);
      console.error("Failed to fetch attendee:", err);
      toast.error("Failed to fetch attendee details");
    });
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-gray-50 p-3 rounded">
        <p className="text-sm"><strong>Attendee:</strong> {attendee?.memberDetails?.fullName || attendee?.fullName}</p>
        <p className="text-sm"><strong>Email:</strong> {attendee?.memberDetails?.email || attendee?.email}</p>
        <p className="text-sm text-gray-600"><strong>Type:</strong> {attendee?.type || 'Member'}</p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="requestType"
            checked={selectedOption === "system_generate"}
            onChange={() => setSelectedOption("system_generate")}
            className="h-4 w-4"
          />
          <span>I'm available for a {connectMeetLabel} for this event</span>
        </label>

        <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="requestType"
            checked={selectedOption === "connect_with_members"}
            onChange={() => setSelectedOption("connect_with_members")}
            className="h-4 w-4"
          />
         <span>Request specific people for my {connectMeetLabel} for this event</span>
        </label>

        {selectedOption === "connect_with_members" && (
          <div className="ml-6 p-3 bg-gray-50 rounded">
            <p className="text-sm mb-2 font-medium">Select members to request:</p>
            <div className="max-h-40 overflow-y-auto">
              {availableMembers.map((member: any) => (
                <label key={member.id} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-gray-100 rounded">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.id)}
                    onChange={(e) => handleMemberToggle(member.id, member.name, e.target.checked, member.userId)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{member.name}</span>
                </label>
              ))}
            </div>
            {availableMembers.length === 0 && (
              <p className="text-sm text-gray-500">No other attendees available</p>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="requestType"
            checked={selectedOption === "not_interested"}
            onChange={() => setSelectedOption("not_interested")}
            className="h-4 w-4"
          />
          <span>I'm NOT available for a {connectMeetLabel} for this event</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onComplete}
          className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-[#EF7A2B] text-white rounded-lg hover:bg-orange-600"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save Request"}
        </button>
      </div>
    </div>
  );
};

const EventDetail = () => {
  interface NewDataItem {
    email: string;
  }
  const [host, setHost]: any = useState()
  const [data, setData]: any = useState()
  const [attendee, setAttendee]: any = useState([])
  const [aloading, setALoader] = useState(false)
  const [isModal, setModal] = useState('')
  const [photoImportOpen, setPhotoImportOpen] = useState(false)
  const [photoMembers, setPhotoMembers] = useState<any[]>([])
  const [photoGuests, setPhotoGuests] = useState<any[]>([])
  const photoFileRef = React.useRef<HTMLInputElement>(null)
  const [isRModal, setRModal] = useState(false)
  const [isRsvpStatusModal, setRsvpStatusModal] = useState(false)
  const [group, setGroup] = useState(false)
  const [memberHistory, setmemberHistory] = useState(false)
  const [invites, setInvites] = useState([])
  const [requestGroup, setRequestGroup] = useState(false)
  const [memberHistoryData, setmemberHistoryData] = useState()
  const [attendeesGroup, setattendeesGroup] = useState()
  const [newData, setNewData] = useState([])
  const [Roledata, setRoleData]: any = useState()
  const user = useSelector((state: any) => state.user);
  const history = useNavigate()
  const navigate = useNavigate();
  const [role, setRole] = useState()
  const [me, setMe] = useState()
  const [member, setMember]: any = useState()
  // resolve event id safely
  const { eventid: eventidParam } = useParams();
  const [id, setId] = useState<string | null>(null);
  const connectMeetLabel = data?.groupId?.connectMeetLabel || user?.connectMeetLabel || 'ConnectMeet';
  const pluralize = (label: string) => {
    if (label.endsWith('s')) return label;
    const words = label.split(' ');
    const last = words[words.length - 1];
    const pluralLast = last.endsWith('y') ? last.slice(0, -1) + 'ies' : last + 's';
    return [...words.slice(0, -1), pluralLast].join(' ');
  };
  const connectMeetLabelPlural = pluralize(connectMeetLabel);
  const testimonialLabel = user?.testimonialLabel || 'Thank You';
  const testimonialLabelPlural = pluralize(testimonialLabel);
  const guestFormDirtyRef = React.useRef(false);
  const memberFormDirtyRef = React.useRef(false);
  const [showManagerRequestModal, setShowManagerRequestModal] = useState(false);
  const [selectedAttendeeForRequest, setSelectedAttendeeForRequest] = useState<any>(null);
  const [attendeeRequestStatuses, setAttendeeRequestStatuses] = useState<{[email: string]: string}>({});
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showSubmitThankYou, setShowSubmitThankYou] = useState(false);
  const [tyReceiverId, setTyReceiverId] = useState('');
  const [tyType, setTyType] = useState('');
  const [tyNote, setTyNote] = useState('');
  const [tySubmitting, setTySubmitting] = useState(false);
  const [tySharedGroups, setTySharedGroups] = useState<any[]>([]);
  const [tySelectedGroupIds, setTySelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    if (!eventidParam) {
      setId(null);
      return;
    }

    (async () => {
      // try decodeURIComponent in case it's URL-encoded
      let rawParam = eventidParam;
      try {
        rawParam = decodeURIComponent(eventidParam);
      } catch (e) {
        rawParam = eventidParam;
      }

      // Try decryptId (resilient decrypt helper should exist). If decryptId returns a valid 24-hex id, use it.
      try {
        const maybe = await decryptId(rawParam); // decryptId now resilient
        if (maybe && /^[0-9a-fA-F]{24}$/.test(maybe)) {
          setId(maybe);
        } else if (maybe) {
          // decrypted but not necessarily a 24-hex id — still set it for APIs that accept other ids
          setId(maybe);
        } else if (/^[0-9a-fA-F]{24}$/.test(rawParam)) {
          // fallback: the param itself is a raw ObjectId
          setId(rawParam);
        } else {
          console.error("Could not resolve event id from param:", eventidParam);
          setId(null);
        }
      } catch (err) {
        // decrypt threw; fallback to raw param if it looks like ObjectId
        if (/^[0-9a-fA-F]{24}$/.test(rawParam)) {
          setId(rawParam);
        } else {
          console.error("decryptId threw and param is not valid ObjectId:", rawParam, err);
          setId(null);
        }
      }
    })();
  }, [eventidParam]);

  const [loadingMessage, setLoadingMessage] = useState(<div></div>);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState('');
  
  const getDetail = () => {
    loader(true)
    ApiClient.get('api/event/details', { id: id, email: user?.email }).then(res => {
      loader(false)
      if (res.success) {
        setData(res.data)
        if (res.data?.addedBy?._id)
          // getHostDetail(res.data.addedBy._id)
          if (res.data?.groupId?._id) getMember(res.data.groupId._id)

      }
    })
  }

  // const getHostDetail = (id: any) => {
  //   ApiClient.get('api/user/detail', { id: id }).then(res => {
  //     if (res.success) {
  //       setHost(res.data)
  //     }
  //   })
  // }

  // cw: match an attendee row to the logged-in user. Email alone is fragile (case/whitespace, or a
  // redux user missing email), so also match on the stable user id against userId/memberId/member _id.
  const emailsEqual = (a?: string, b?: string) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  const matchesCurrentUser = (itm: any) =>
    emailsEqual(itm?.memberDetails?.email || itm?.email, user?.email) ||
    (!!user?._id && (
      String(itm?.userId) === String(user._id) ||
      String(itm?.memberId) === String(user._id) ||
      String(itm?.memberDetails?._id) === String(user._id)
    ));

  const getAttendee = () => {
    let f = {
      eventId: id,
      joinRequest: '',
    }
    setALoader(true)
    ApiClient.get('api/attendees/list', f).then(res => {
      setALoader(false)
      if (res.success) {
        setNewData(res?.data)
        let data: any = res.data.map((itm: any) => {
          return {
            ...itm,
            email: itm?.memberDetails?.email || itm.email
          }
        })

        setAttendee(data)
        let ext = data.find((itm: any) => matchesCurrentUser(itm))
        if (ext) {

          // cw: trying to get the actual role name for GLs
          // console.log("Found current user attendee:", ext);
          // console.log("User type:", ext.type);
          // console.log("Member details role:", ext.memberDetails?.role);
          // console.log("Attendee role:", ext.attendeeRole);

          // cw: INCORRECT -- og supposed to give everyone without memberDetails the "member" role
          // cw: but this doesnt consider the case of group leaders since theyre not added to the addmembers table, thus losing their gl permissions
          //setRole(ext?.memberDetails?.role || 'member')

          // cw: check the type value in attendees table. {type: "Group Leader"}. If there's nothing, default to the "member" role
          setRole(ext?.type || ext?.memberDetails?.role || 'member')
          setMe(ext)
        }
      }
    })
  }

  // cw: auto-refresh attendee list every 4 seconds for live check-in updates
  // only update state if data has actually changed to prevent UI shifting
  // pauses while any modal is open to avoid re-render conflicts
  useEffect(() => {
    if (!id) return;
    if (isRModal || showThankYouModal || showManagerRequestModal || showSubmitThankYou) return;

    const interval = setInterval(() => {
      ApiClient.get('api/attendees/list', { eventId: id, joinRequest: '' }).then(res => {
        if (res.success) {
          let newData = res.data.map((itm: any) => ({
            ...itm,
            email: itm?.memberDetails?.email || itm.email
          }));

          // Only update state if data has actually changed
          setAttendee((prevAttendee: any[]) => {
            const prevIds = prevAttendee.map((a: any) => `${a.id || a._id}-${a.isPresent}-${a.meetConfirm}`).sort().join(',');
            const newIds = newData.map((a: any) => `${a.id || a._id}-${a.isPresent}-${a.meetConfirm}`).sort().join(',');

            if (prevIds !== newIds) {
              return newData;
            }
            return prevAttendee;
          });
        }
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [id, isRModal, showThankYouModal, showManagerRequestModal, showSubmitThankYou]);

  const getInvites = () => {
    let f = {
      eventId: id,
      joinRequest: 'pending',
    }
    ApiClient.get('api/attendees/list', f).then(res => {
      if (res.success) {
        setInvites(res.data)
      }
    })
  }


  // const getAttendeesGroupPair = () => {
  //   // loader(true)
  //   ApiClient.get(`api/event/groups?eventId=${id}`).then(res => {
  //     if (res.success) {
  //       setattendeesGroup(res?.data)
  //       const stateData = {
  //         eventDetail: { attendeesGroup },
  //         dataa: { data },
  //         eventId: { id },
  //         attendeesGroup: attendeesGroup

  //       }
  //       history(`/groupsDetail/${encryptId(id)}`, { state: stateData });
  //       // loader(false)
  //     }
  //   })
  // }
  const getAttendeesGroupPair = () => {
    setLoading(true);
    setLoadingMessage(<div className="h-32 flex items-center justify-center">
      <div className="loader_shine"></div>
      <p>Generating groups...</p>
    </div>);

    ApiClient.get(`api/event/groups?eventId=${id}`).then(async (res) => {
      if (res.success) {
        const stateData = {
          eventDetail: { attendeesGroup: res?.data },
          dataa: { data },
          eventId: { id },
          attendeesGroup: res?.data,
        };

        const encryptedId = await encryptId(id);
        setTimeout(() => {
          history(`/groupsDetail/${encryptedId}`, { state: stateData });
        }, 100);
      } else {
        // cw: Show specific error for only one person checked in
        if (res.error?.message?.includes("one person") || res.message?.includes("one person")) {
          toast.error(`${connectMeetLabelPlural} cannot be generated with one or less people checked in.`);
        } else {
          toast.error(res.message || res.error?.message || `Failed to generate ${connectMeetLabelPlural}`);
        }
      }
      setLoading(false);
    }).catch((error) => {
      console.error('Error:', error);
      const errorMsg = error?.response?.data?.error?.message || error?.response?.data?.message || '';
      if (errorMsg.includes("one person")) {
        toast.error(`${connectMeetLabelPlural} cannot be generated with one or less people checked in.`);
      } else {
        toast.error(`Failed to generate ${connectMeetLabelPlural}`);
      }
      setLoadingMessage(<div></div>);
      setLoading(false);
    });
  };

  const getMemberHistory = () => {
    ApiClient.get(`api/member/event/groups?email=${user?.email}`).then(res => {
      if (res.success) {
        setmemberHistoryData(res?.data)
      }
    })
  }


  const attendeeFilter = (meetConfirm: any) => {
    let arr = attendee || []

    if (meetConfirm == 'Pending') {
      arr = attendee.filter((itm: any) => itm.meetConfirm == 'Pending' || !itm.meetConfirm)
    } else {
      arr = attendee.filter((itm: any) => itm.meetConfirm == meetConfirm)
    }
    return [...arr].sort((a: any, b: any) => {
      const nameA = (a?.memberDetails?.fullName || a?.fullName || '').toLowerCase();
      const nameB = (b?.memberDetails?.fullName || b?.fullName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    })
  }

  const endEvent = () => {
    // if (window.confirm("Do you want to End this Event")) {
    //   loader(true)
    //   ApiClient.put('api/event/edit', { id: id, meetingStatus: "completed" }).then(res => {
    //     if (res.success) {
    //       getDetail()
    //     } else {
    //       loader(false)
    //     }
    //   })
    // }
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to End this Event`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes"
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true)
        ApiClient.put('api/event/edit', { id: id, meetingStatus: "completed" }).then(res => {
          if (res.success) {
            getDetail()
          } else {
            loader(false)
          }
        })
        //   Swal.fire({
        //     icon: "success"
        //   });
      }
    });
  }

  const [resendingRsvp, setResendingRsvp] = useState(false);

  const resendRsvpToPending = () => {
    const pendingCount = attendeeFilter('Pending').length;
    if (pendingCount === 0) {
      toast.info('No pending attendees to resend to');
      return;
    }
    Swal.fire({
      title: "Resend RSVP Emails?",
      text: `This will resend the RSVP email to ${pendingCount} attendee(s) who have not responded yet.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Resend"
    }).then((result) => {
      if (result.isConfirmed) {
        setResendingRsvp(true);
        ApiClient.post('api/attendees/resend-rsvp', {
          eventId: id,
          groupId: data?.groupId?._id
        }).then((res: any) => {
          setResendingRsvp(false);
          if (res.success) {
            toast.success(res.message || `RSVP emails resent to ${res.sent} attendee(s)`);
          } else {
            toast.error(res.message || 'Failed to resend RSVP emails');
          }
        }).catch(() => {
          setResendingRsvp(false);
          toast.error('Failed to resend RSVP emails');
        });
      }
    });
  };

  const deletePremit = (row: any) => {
    let value = false
    if (row?.addedBy === user._id || data?.addedBy?._id === user?._id) value = true
    // Check if user's customerRole is in the groupLeaderRoleIds array (GL + Assistant GL)
    const roleId = getUserRoleInEventGroup();
    if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
    if (roleId && environment.connectmeetmanagerRoleId === roleId) value = true
    if (data?.meetingStatus == 'completed') value = false
    return value
}

const getUserRoleInEventGroup = () => {
  const eventGroupId = data?.groupId?._id || data?.groupId;
  if (!eventGroupId || !user?.groups) return null;
  const group = user.groups.find((g: any) => {
      const gid = g.groupId?._id || g.groupId;
      return gid === eventGroupId && g.status === 'active';
  });
  if (!group) return null;
  return group.customerRole?._id || group.customerRole;
};

// cw: helper function, both gl and assistant gl are groupleaders
const isGroupLeader = () => {
  const roleId = getUserRoleInEventGroup();
  return roleId && environment.groupLeaderRoleIds.includes(roleId);
}

const addPremit = () => {
  // cw: guests never manage an event (Invite Member/Guest, Manage Check-ins, Manage {CM} Requests).
  if (user?.isGuest) return false
  let value = false
  const roleId = getUserRoleInEventGroup();
  if (data?.addedBy?._id === user._id) value = true
  if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
  if (roleId && environment.connectmeetmanagerRoleId === roleId) value = true

  return value
}

// cw: Parse an uploaded photo-list PDF, then split parsed people into existing
// group members vs guests and pre-populate the Invite Member / Invite Guest popups.
const handlePhotoListFile = async (e: any) => {
  const file = e.target.files?.[0]
  e.target.value = '' // allow re-selecting the same file later
  if (!file) return
  loader(true)
  const res = await ApiClient.importPhotoList({ file })
  const people = res?.success ? (res.data || []) : []
  if (!res?.success) {
    loader(false)
    toast.error(res?.message || 'Could not read the photo list')
    return
  }
  if (!people.length) {
    loader(false)
    toast.warn('No names and emails were found in that file')
    return
  }
  // Fetch this group's members to decide member vs guest
  const groupId = data?.groupId?._id || data?.groupId
  const mRes = await ApiClient.get('api/members/list', { groupId, isDeleted: false })
  loader(false)
  const groupMembers = (mRes?.success && mRes.data) ? mRes.data : []
  const byEmail: any = {}
  groupMembers.forEach((u: any) => { if (u.email) byEmail[u.email.toLowerCase()] = u })

  const membersSeed: any[] = []
  const guestsSeed: any[] = []
  people.forEach((p: any) => {
    const match = byEmail[(p.email || '').toLowerCase()]
    if (match) {
      // memberId + name are resolved inside AddAttendee against its dropdown list by email.
      // Seed with the account's fullName-derived name (members/list lowercases lastName).
      const full = (match.fullName || `${match.firstName || ''} ${match.lastName || ''}`).trim()
      const parts = full.split(/\s+/)
      membersSeed.push({ email: match.email, firstName: parts[0] || match.firstName || '', lastName: parts.slice(1).join(' ') || match.lastName || '' })
    } else {
      guestsSeed.push({ email: p.email, firstName: p.firstName, lastName: p.lastName, fullName: `${p.firstName} ${p.lastName}`.trim() })
    }
  })

  setPhotoMembers(membersSeed)
  setPhotoGuests(guestsSeed)
  setPhotoImportOpen(true)
}

const gl = () => {
  let value = false
  const roleId = getUserRoleInEventGroup();
  if (data?.addedBy?._id === user._id) value = true
  if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
      
      // console.log('addPremit check:', {
      //   isCreator: data?.addedBy?._id === user._id,
      //   role: role,
      //   isInGLArray: environment.groupLeaderRoleIds.includes(user?.customerRole?._id),
      //   finalValue: value
      // });
      
      return value
  }

  const assistantMeetManager = () => {
    let value = false
    const roleId = getUserRoleInEventGroup();
    if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
    if (roleId && environment.connectmeetmanagerRoleId === roleId) value = true

    return value
}

// cw: ONLY TY Tracker + GL + AGL can manage Thank Yous
const canManageThankYous = () => {
    const roleId = getUserRoleInEventGroup();
    if (!roleId) return false;
    if (environment.thankYouRoleIds?.includes(roleId)) return true;
    return false;
}

  const getMember = (groupId = '') => {
    let f = {
      userId: user._id,
      groupId: groupId,
      count: 1,
      page: 1
    }
    ApiClient.get('api/members/list', f).then(res => {
      if (res.success) {
        let data = res.data?.[0]
        setMember(data)
      }
    })
  }

  const deleteItem = (id: any) => {
    // if (window.confirm("Do you want to delete this")) {
    //   loader(true)
    //   ApiClient.delete('api/attendees', { id: id }).then(res => {
    //     if (res.success) {
    //       getAttendee()
    //     }
    //     loader(false)
    //   })
    // }
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes"
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true)
        ApiClient.delete('api/attendees', { id: id }).then(res => {
          if (res.success) {
            getAttendee()
          }
          loader(false)
        })
        //   Swal.fire({
        //     icon: "success"
        //   });
      }
    });
  }

  const markAttendance = () => {
    loader(true)
    ApiClient.post('api/sentEmail', { eventId: id }).then(res => {
      loader(false)
      if (res.success) {
        toast.success(res.message)
        getDetail()
      }
    })
  }

  useEffect(() => {
    if (!id) return; // wait until id is resolved
    getDetail();
    getAttendee();
    getInvites();
    getMemberHistory();
    // getAttendeesGroupPair()
  }, [id]);
  

  // cw: solely for debugging
  // useEffect(() => {
  //   console.log("User customerRole._id type:", typeof user?.customerRole?._id);
  //   console.log("User customerRole._id value:", user?.customerRole?._id);
  //   console.log("Environment GLs:", environment.groupLeaderRoleIds);
  // }, [user]);

  // useEffect(() => {
  //   console.log("CMM Debug - isGroupGenerated:", data?.isGroupGenerated);
  //   console.log("CMM Debug - isMarkAttendance:", data?.isMarkAttendance);
  //   console.log("CMM Debug - meetingStart():", meetingStart());
  // }, [data]);

  // cw: helper func to format countdown as months/week/day/hr
  const formatCountdown = (totalHours: number): string => {
    const months = Math.floor(totalHours / (24 * 30));
    const weeks = Math.floor((totalHours % (24 * 30)) / (24 * 7));
    const days = Math.floor((totalHours % (24 * 7)) / 24);
    const hours = Math.floor(totalHours % 24);
    
    const parts = [];
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (weeks > 0) parts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? 's' : ''}`);
    
    return parts.slice(0, 2).join(', ') || 'Starting soon';
  };

  // cw: Meeting countdown will be live, no need to refresh
  useEffect(() => {
    const timer = setInterval(() => {
        if (!data?.date) return;
        
        const now = new Date();
        const eventTime = new Date(data.date);
        const diffMs = eventTime.getTime() - now.getTime();
        
        if (diffMs <= 0) {
            setCountdown("Event started");
            clearInterval(timer);
            return;
        }
        
        const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        
        // For events >24 hours away, show only larger units
        if (totalHours >= 24) {
            const days = Math.floor(totalHours / 24);
            const months = Math.floor(days / 30);
            const weeks = Math.floor((days % 30) / 7);
            const remainingDays = days % 7;
            
            let display = "Meeting will start in ";
            if (months > 0) display += `${months} month${months > 1 ? 's' : ''}, `;
            if (weeks > 0) display += `${weeks} week${weeks > 1 ? 's' : ''}, `;
            if (remainingDays > 0) display += `${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
            
            setCountdown(display.replace(/, $/, ''));
        } else if (totalMinutes <= 10) {
            // Show seconds when 10 minutes or less
            const minutes = Math.floor(diffMs / (1000 * 60));
            const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
            setCountdown(`Meeting will start in ${minutes} min ${seconds} sec`);
        } else {
            // Show precise time for <24 hours
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            setCountdown(`Meeting will start in ${totalHours} hours, ${minutes} minutes`);
        }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [data?.date]);

  // Fetch all request statuses when manage modal opens
  useEffect(() => {
    if (showManagerRequestModal && id) {
      // cw: Refresh attendee list to get latest check-in status
      getAttendee();
      
      const fetchStatuses = async () => {
        try {
          const res = await ApiClient.get("api/group-request/list", { eventId: id });
          if (res?.success && res?.data) {
            const statusMap: {[email: string]: string} = {};
            res.data.forEach((req: any) => {
              if (req.requestedBy) {
                statusMap[req.requestedBy] = req.requestType || 'system_generate';
              }
            });
            setAttendeeRequestStatuses(statusMap);
          }
        } catch (err) {
          console.error("Failed to fetch request statuses:", err);
        }
      };
      fetchStatuses();
    }
  }, [showManagerRequestModal, id]);

  const getRequestStatus = (email: string) => {
    return attendeeRequestStatuses[email] || 'system_generate';
  };

  const requestCheck = () => {
    let value = false
    if (data?.addedBy?._id != user._id && !me) value = true
    return value
  }

  const request = () => {
    if (!member) {
      toast.error("You are not a member of this Group")
      return
    }
    let payload = {
      eventId: id,
      groupId: data?.groupId?._id || '',
      memberId: member._id,
      fullName: user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      email: user?.email
    }

    loader(true)
    ApiClient.post('api/event/request', payload).then(res => {
      loader(false)
      if (res.success) {
        getDetail()
        toast.success(res.message)
      }
    })
  }

  const acceptReject = (id: any, joinRequest = '') => {
    // if (window.confirm(`Do you want to ${joinRequest == 'accepted' ? 'Accept' : 'Reject'} this request`)) {
    //   loader(true)
    //   ApiClient.put('api/accept-reject/request', { id: id, status: joinRequest }).then(res => {
    //     loader(false)
    //     if (res.success) {
    //       toast.success(`Request ${joinRequest} successfully`)
    //       getAttendee()
    //       getInvites()
    //     }
    //   })
    // }
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${joinRequest == 'accepted' ? 'Accept' : 'Reject'} this request`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes"
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true)
        ApiClient.put('api/accept-reject/request', { id: id, status: joinRequest }).then(res => {
          loader(false)
          if (res.success) {
            toast.success(`Request ${joinRequest} successfully`)
            getAttendee()
            getInvites()
          }
        })
        //   Swal.fire({
        //     icon: "success"
        //   });
      }
    });
  }

  const isButtonVisible = () => {
    const currentTime = new Date();
    const startTime = new Date(datepipeModel.datetime(data?.date));
    const fifteenMinutesBefore = new Date(startTime.getTime() - 15 * 60 * 1000);

    return currentTime >= fifteenMinutesBefore && currentTime <= startTime;
  };

  // const isButtonVisible = () => {
  //   const currentTime = new Date();
  //   const startTime = new Date(datepipeModel.datetime(data?.date));
  //   const fifteenMinutesBefore = new Date(startTime.getTime() - 15 * 60 * 1000);

  //   // Check if current time is before the start time or within 15 minutes before the start time
  //   return currentTime >= fifteenMinutesBefore;
  // };
  
  // cw: replaced entire meetingStart() with "Event started" logic
  const meetingStart = () => {
    if (!data?.date) return false;
    
    const now = new Date();
    const eventTime = new Date(data?.date);
    
    // Meeting has started if current time is past event time
    return now.getTime() >= eventTime.getTime();
  }

  function handlePrint() {
    const imgElement = document.createElement("img");
    imgElement.src = data?.qrCode;

    const printWindow = window.open("", "_blank") as Window;
    if (printWindow) {
      printWindow.document.write(`<html><head><title>Print Image</title></head><body style='margin: 0; text-align: center;'><img src='${data?.qrCode}' style='max-width: 100%; max-height: 100%;'></body></html>`);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    } else {
      console.error("Failed to open print window.");
    }
  }


  const handleOpenModal = (e: any) => {
    if (e === "GenerateConnectmeets") {
        // Fetch fresh attendee data before showing popup
        loader(true);
        ApiClient.get("api/attendees/list", { eventId: id }).then(res => {
            loader(false);
            if (res.success) {
                const freshAttendees = res.data;
                const checkedInCount = freshAttendees.filter((itm: any) => itm.isPresent === true).length;
                const rsvpYesCount = freshAttendees.filter((itm: any) => itm.meetConfirm === 'Yes').length;
                
                Swal.fire({
                  title: `Generate ${connectMeetLabelPlural}`,
                    html: `
                        <div style="text-align: left; padding: 10px;">
                            <p style="margin-bottom: 10px;"><strong>RSVP'd Yes:</strong> ${rsvpYesCount} people</p>
                            <p style="margin-bottom: 15px;"><strong>Checked In:</strong> ${checkedInCount} people</p>
                            <hr style="margin: 15px 0;">
                            <p style="margin-top: 15px;"><strong>Generate groups with ${checkedInCount} checked-in attendees?</strong></p>
                        </div>
                    `,
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#6c757d",
                    confirmButtonText: "Yes, Generate Groups",
                    cancelButtonText: "No, Manage Check-ins"
                }).then((result) => {
                    if (result.isConfirmed) {
                        getAttendeesGroupPair();
                    } else if (result.dismiss === Swal.DismissReason.cancel) {
                        setRModal(true);
                    }
                });
            }
        });
    }
    else if (e === "SeeAllGroups") {
        const stateData = {
            eventDetail: { attendeesGroup },
            dataa: { data },
            eventId: { id },
            attendeesGroup: attendeesGroup
        };
        encryptId(id).then(encrypted => {
            history(`/groupsDetail/${encrypted}`, { state: stateData });
        });
    }
}

  interface NewDataItem {
    memberDetails?: {
      email?: string;
    };
    // Add other properties if needed
  }


  function formatDateToTimezone(date: any, timeZone: any) {
    return datepipeModel.formatDateToTimezone(date, timeZone)
  }

  const isInvited = () => {
    let arr = attendeeFilter('Pending') || []
    let ext = arr.find((itm: any) => matchesCurrentUser(itm))
    let value = false
    //if (ext && data?.addedBy?.email != user.email) value = true
    if (ext) value = true  // cw: show the Yes No confirm you are attending button for everyone invited, including the event creator
    return value
  }

  const confirmPopup = (p = '') => {
    let payload = {
      eventId: id,
      userId: user._id,
      status: p
    }

    loader(true)
    ApiClient.put('api/confirm/meet', payload).then(res => {
      loader(false)
      if (res.success) {
        toast.success(res.message)
        getAttendee()
        getInvites()
      }
    })
  }

  // Login-free QR check-in: point the QR at the public email+code page carrying the event code.
  const qrUrl = `${window.location.origin}/event-checkin?code=${encodeURIComponent(data?.checkinCode || '')}`;
  //console.log("QR Code URL:", qrUrl);

  return (
    <>
      <PageLayout>
        {loading ? <>
          <div className="loader_spin"></div>
          {loadingMessage}
        </> : <> <div className="bg-white border-gray-200 px-4 pt-8 lg:px-6 py-2.5 dark:bg-gray-800">
          <div className="container mx-auto">
            <Link to="/event" >
              <div
                className="mb-2 cp pt-1 ms-2 pointer"
              // onClick={() => history.go(-1)}
              >
                <span>


                  <i className="fa fa-arrow-left mr-2"></i>
                </span>
                Back
              </div>
            </Link>


          </div>
        </div>

        


          <div className="bg-white border-gray-200 px-4 lg:px-6 py-8 dark:bg-gray-800">
            <div className="container mx-auto">
              <div className="events_details">
                <div className="grid grid-cols-12 gap-x-2 xl:gap-x-10">
                  <div className="col-span-12 md:col-span-7 lg:col-span-8 bg-gray-100/50 px-8 rounded-md">
                    <div className="flex flex-col gap-y-2 lg:gap-y-3 py-2 lg:py-4 border-b-2 border-[#EF7A2B]">
                      <div className="date_text">
                        {/* <h6 className="text-[#EF7A2B] text-[16px] xl:text-[24px] font-[400]"> <span className="mr-1">{datepipeModel.datetime(data?.date)}</span>
                       
                      </h6> */}
                        <h6 className="text-[#EF7A2B] text-[16px] xl:text-[24px] font-[400]">
                          <span className="mr-1">
                            {datepipeModel.formatDateToTimezone(data?.date, data?.timezone)}, {data?.timezone?.replace(/_/g, ' ')}
                          </span>
                        </h6>

                      </div>
                      <div>
                        <p className="text-[#393C3D] text-[20px] lg:text-[33px] font-[600] capitalize">{data?.title}</p>
                      </div>
                      <div className="flex gap-4 flex-col">
                        <p className="flex gap-x-2 items-start capitalize"><IoLocationSharp className="text-orange-500 text-2xl lg:text-3xl shrink-0" /><span>{(data?.address || '').split('\n').map((line: string, i: number) => (<span key={i} className="block">{line}</span>))}</span></p>
                        {data?.externalLink ? <a href={data?.externalLink} target="_blank" className="flex gap-x-2 items-center "><MdInsertLink className="text-orange-500 text-2xl lg:text-3xl" />{data?.externalLink}</a> : ""}
                        <p className="shrink-0">Event Type: <span className="text-orange-500 capitalize">{data?.type === "inPerson" ? "In-Person" : data?.type === "virtual" ? "Virtual" : ""}</span></p>

                      </div>

                    </div>
                    <div className="main_decription flex flex-col lg:gap-y-10 py-8 lg:py-15">
                      <div className="ql-editor text-[#0B0A0A] text-[19px] leading-5 lg:leading-8 font-[400]" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: data?.description }}></div>
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-5 lg:col-span-4 ">
                    <div className="flex items-center gap-x-6 gap-y-6">
                      <div className=" border-4 border-orange-500 rounded-full drop-shadow-lg">
                        <img className="h-28 w-28 rounded-full object-cover " src="/assets/img/event-banner.png" alt="" />
                      </div>
                      <div className="teaxings">
                        <h6 className="text-[#3F3F3F] text-[18px] font-[700]">{data?.groupLeaderDetail?.fullName}</h6>
                        <div className="flex flex-col gap-y-2 mb-2">
                          <span className="relative text-orange-500 capitalize">
                            {environment.groupLeaderRoleIds.includes(data?.addedBy?.customerRole?._id) 
                              ? "Group Leader" 
                              : data?.addedBy?.customerRole?.name || "Event Host"}
                          </span>
                          <span className="block h-1 w-12 bg-orange-500 "></span>
                        </div>
                        {(!user?.isGuest && (
                        environment.groupLeaderRoleIds.includes(user?.customerRole?._id) ||
                        environment.connectmeetmanagerRoleId === user?.customerRole?._id ||
                        user?.groups?.some((g: any) => g.status === 'active' &&
                          (environment.groupLeaderRoleIds.includes(g.customerRole?._id || g.customerRole) ||
                          environment.connectmeetmanagerRoleId === (g.customerRole?._id || g.customerRole))) ||
                        data?.memberId?.role === "assistant" ||
                        role === "assistant" ||
                        role === "meetManager")) ? <div className=" flex flex-col gap-y-1 mt-4">
                          {/* <p className="font-[400]">You Have RSVP'd . Yes to this event</p> */}
                          <p className="text-[#2B91EF] font-[600] cursor-pointer text-[16px]" onClick={() => setRModal(true)}>Check-in List ({attendee.filter((a: any) => a.isPresent === true).length})</p>
                        </div> : ""}

                        {/* <div><button className="text-sm hover:text-blue-500 underline text-blue-500 underline-offset-4" onClick={() => setmemberHistory(true)}> Member history</button></div> */}
                      </div>
                    </div>
                    {data?.qrCode != "" ? <>
                      <div className="md:mt-8 lg:mt-10">
                        <div className="borders_data p-6 flex items-center justify-between gap-x-4">
                          {data?.meetingStatus != 'completed' ? <div>
                            <img src={data?.qrCode}></img>
                          </div> : <div>
                            <img className="w-full blur-[2px]	" src={data?.qrCode}></img>
                          </div>}

                          <div>

                            <div className="flex flex-col items-center justify-center gap-y-4">
                              <button className="bg-[#EF7A2B] w-44 py-3 flex items-center justify-center gap-x-2 text-white shadow hover:shadow-lg rounded-xl" onClick={handlePrint}><FiPrinter /> Print</button>
                              {/* <button className="bg-[#46454E] w-44 py-3 flex items-center justify-center gap-x-2 text-white shadow hover:shadow-lg rounded-xl"  onClick={() => window.open(data?.qrCode, '_blank')}><MdOutlineShare />Share</button> */}
                            </div>
                          </div>



                        </div>
                      </div>
                    </> : ""}

                    <div className="mt-6 md:mt-8 lg:mt-10">
                      <div className="borders_data p-6">

                        {isInvited() ? <>
                          <div>Invited, please confirm whether you will attend or not.</div>
                          <div className="mb-3 flex gap-2">
                            <button type="button" onClick={() => confirmPopup('Yes')} className="bg-green-600 px-[15px] py-[5px] text-white text-[12px] rounded-[5px]">Yes</button>
                            <button type="button" onClick={() => confirmPopup('No')} className="bg-[#C22020] px-[15px] py-[5px] text-white text-[12px] rounded-[5px]">No</button>
                          </div>
                        </> : (() => {
                          const myAttendee = attendee.find((itm: any) => (itm.memberDetails?.email || itm.email) === user.email);
                          const myRsvp = myAttendee?.meetConfirm;
                          if (myRsvp === 'Yes' || myRsvp === 'No') {
                            return (
                              <div className="mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span>Your RSVP:</span>
                                  <span className={`font-semibold ${myRsvp === 'Yes' ? 'text-green-600' : 'text-red-600'}`}>{myRsvp}</span>
                                </div>
                                {data?.meetingStatus !== 'completed' && (
                                  <button
                                    type="button"
                                    onClick={() => confirmPopup(myRsvp === 'Yes' ? 'No' : 'Yes')}
                                    className={`${myRsvp === 'Yes' ? 'bg-[#C22020]' : 'bg-green-600'} px-6 py-2.5 text-white text-sm font-medium rounded-lg`}
                                  >
                                    Change to {myRsvp === 'Yes' ? 'No' : 'Yes'}
                                  </button>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        <div className="flex flex-col gap-y-4">
                          {/* <button onClick={() => {
                            console.log("=== BUTTON DEBUG ===");
                            console.log("data?.addedBy?._id:", data?.addedBy?._id);
                            console.log("user._id:", user._id);
                            console.log("Is creator:", data?.addedBy?._id === user._id);
                            console.log("role:", role);
                            console.log("data?.isMarkAttendance:", data?.isMarkAttendance);
                            console.log("meetingStart():", meetingStart());
                          }} style={{background: 'red', color: 'white', padding: '10px'}}>
                            DEBUG BUTTON
                          </button> */}

                          {requestCheck() && data?.meetingStatus !== "completed" ? <>
                            <button className="bg-[#46454E] py-3 px-2 text-center text-white rounded-lg" disabled={data?.isRequested ? true : false} onClick={request}>{data?.joinRequest === "rejected" ? "Rejected": data?.isRequested ? 'Requested' : 'Request to Join'}</button>
                          </> : <></>}
                          {/* cw: QR Code for login-free self check-in */}
                          <div className="mb-4 text-center">
                            <div className="mb-2 font-semibold">Check-in</div>
                            <img
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}`}
                              alt="Check-in QR Code"
                              className="mx-auto border-2 border-gray-300 rounded"
                            />
                            <div className="font-semibold mt-1">Scan to check in</div>
                            {data?.checkinCode ? (
                              <div className="mt-2 text-center">
                                <a href={qrUrl} target="_blank" rel="noreferrer" className="font-medium text-blue-600 underline">Check-in link</a>
                              </div>
                            ) : null}
                          </div>

                          {meetingStart() ? (
                            <div className="text-green-500">Meeting Started</div>
                          ) : (
                            // cw: Meeting will start in [countdown time]
                            <div className="text-red-500">{countdown}</div>
                          )}

                          {data?.meetingStatus != 'completed' ? <>

                            {addPremit() ? <>
                              {data?.groupId?.photoList ? <>
                                <button className="bg-[#46454E] py-3 px-2 text-center text-white rounded-lg" onClick={() => photoFileRef.current?.click()}>Import Photo List</button>
                                <input ref={photoFileRef} type="file" accept="application/pdf" className="hidden" onChange={handlePhotoListFile} />
                              </> : <></>}
                              <button className="bg-[#46454E] py-3 px-2 text-center text-white rounded-lg" onClick={() => setModal('member')}>Invite Member</button>
                              <button className="bg-[#46454E] py-3 px-2 text-center text-white rounded-lg" onClick={() => setModal('guest')}>Invite Guest</button>
                              <button className="bg-[#0E9F6E] py-3 px-2 text-center text-white rounded-lg" onClick={() => setRModal(true)}>Manage Check-ins ({attendee.filter((a: any) => a.isPresent === true).length})</button>
                              <button className="bg-[#2B91EF] py-3 px-2 text-center text-white rounded-lg" onClick={() => setRsvpStatusModal(true)}>Check RSVP Status</button>
                              <button className="bg-[#5B5BD6] py-3 px-2 text-center text-white rounded-lg" onClick={() => setShowManagerRequestModal(true)}>Manage {connectMeetLabel} Requests</button>
                            </> : <></>}
                            {canManageThankYous() ? <>
                              <button className="bg-[#22C55E] py-3 px-2 text-center text-white rounded-lg" onClick={() => setShowThankYouModal(true)}>Manage {testimonialLabelPlural}</button>
                            </> : <></>}
                            {attendeeFilter('Yes').length ? <>
                              {/* {assistantMeetManager() ? <>
                              {meetingStart()  ? <>
                                <button className="bg-[#EF7A2B] py-3 px-2  text-center text-white rounded-lg" onClick={markAttendance}>Mark Attendance</button>
                              </> : <></>}

                              {meetingStart() ? <>
                                <button className="bg-[#46454E] py-3 px-2 text-center text-white rounded-lg" onClick={endEvent}>End Meeting</button>
                              </> : <></>}
                              
                            </> : <></>} */}
                              {gl() || assistantMeetManager() ? <>
                                {/* cw: Mark Attendance button removed */}

                                {meetingStart() ? <>
                                  <button className="bg-[#46454E] py-3 px-2 text-center text-white rounded-lg" onClick={endEvent}>End Meeting</button>
                                </> : <></>}
                              </> : <></>}
                            </> : <></>}
                          </> : <>
                            <div className="text-black text-center font-bold bg-gray-200 py-2 rounded-md flex items-center justify-center gap-2"><span><RiRestTimeLine /></span> Meeting Ended</div>
                            {canManageThankYous() && data?.updatedAt && (Date.now() - new Date(data.updatedAt).getTime() < 24 * 60 * 60 * 1000) ? <>
                              <button className="bg-[#22C55E] py-3 px-2 text-center text-white rounded-lg" onClick={() => setShowThankYouModal(true)}>Manage {testimonialLabelPlural}</button>
                            </> : <></>}
                          </>}


                        </div>


                        {aloading ? <>
                          <div className="shine mb-3 mt-3"></div>
                          <div className="shine mb-3"></div>
                          <div className="shine mb-3"></div>
                          <div className="shine mb-3"></div>
                          <div className="shine mb-3"></div>
                          <div className="shine mb-3"></div>
                        </> : <>
                          <div className="mt-6 mb-6">
                            <h6 className="text-green-600 text-[19px] leading-2  lg:leading-6	 font-[600]">YES ({attendeeFilter('Yes').length})</h6>
                            <ul className="mt-3 custom-scrollbar pr-5">
                              {attendeeFilter('Yes').map((itm: any) => {
                                const _uid = itm?.userId || itm?.memberDetails?._id;
                                const _name = <span className="capitalize">{itm?.memberDetails?.fullName || itm?.fullName}</span>;
                                return <>
                                 <li className="text-[#3F3F3F] text-[14px] flex border-b py-3">
                                 <span>{_uid ? <Link to={`/user-profile/${_uid}?groupId=${data?.groupId?._id || data?.groupId}`} className="text-orange-600 hover:underline">{_name}</Link> : _name} ({itm?.email || itm.memberDetails?.email || 'Guest'})</span>

                                    {/* {deletePremit(itm) ? <>
                                    <Tooltip placement="top" title="Delete" className='cursor-pointer ml-auto text-red-500'> <span onClick={() => deleteItem(itm.id)} >
                                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/><path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/></svg>
                                    </span> </Tooltip>
                                  </> : <></>} */}
                                  </li>
                                </>
                              })}

                            </ul>
                          </div>

                          <div className="mt-6 mb-6">
                            <h6 className="text-[#C22020] text-[19px] leading-2  lg:leading-6	 font-[600]">NO ({attendeeFilter('No').length})</h6>
                            <ul className=" mt-3 custom-scrollbar pr-5">
                              {attendeeFilter('No').map((itm: any) => {
                                const _uid = itm?.userId || itm?.memberDetails?._id;
                                const _name = <span className="capitalize">{itm?.memberDetails?.fullName || itm?.fullName}</span>;
                                return <>
                                  <li className="text-[#3F3F3F] text-[14px] flex border-b py-3">
                                  <span>{_uid ? <Link to={`/user-profile/${_uid}?groupId=${data?.groupId?._id || data?.groupId}`} className="text-orange-600 hover:underline">{_name}</Link> : _name} ({itm?.email || itm.memberDetails?.email || 'Guest'})</span>

                                    {/* {deletePremit(itm) ? <>
                                    <Tooltip placement="top" title="Delete" className='cursor-pointer ml-auto text-red-500'> <span onClick={() => deleteItem(itm.id)} >
                                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/><path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/></svg>
                                    </span> </Tooltip>
                                  </> : <></>} */}
                                  </li>
                                </>
                              })}
                            </ul>
                          </div>

                          <div className="mt-6 mb-6">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h6 className="text-[#C22020] text-[19px] leading-2  lg:leading-6	 font-[600]">VIEWED/NO RESPONSE YET ({attendeeFilter('Pending').length})</h6>
                              {addPremit() && attendeeFilter('Pending').length > 0 && (
                                <button
                                  onClick={resendRsvpToPending}
                                  disabled={resendingRsvp}
                                  className="text-[12px] px-3 py-1 bg-[#C22020] text-white rounded hover:bg-[#a01a1a] disabled:opacity-50"
                                >
                                  {resendingRsvp ? 'Sending...' : 'Resend RSVP Email'}
                                </button>
                              )}
                            </div>
                            <ul className="mt-3 grid grid-cols-1 custom-scrollbar">
                              {attendeeFilter('Pending').map((itm: any) => {
                                const _uid = itm?.userId || itm?.memberDetails?._id;
                                const _name = itm?.memberDetails?.fullName || itm?.fullName;
                                return <>
                                  <li className="text-[#3F3F3F] gap-2 text-[14px] border-b flex items-center py-3">
                                    <FiUser className="text-xl" />
                                    <div>
                                      <p className="flex items-center gap-1 text-[13px] font-semibold capitalize">{_uid ? <Link to={`/user-profile/${_uid}?groupId=${data?.groupId?._id || data?.groupId}`} className="text-orange-600 hover:underline">{_name}</Link> : _name}</p>
                                      <span className=" text-[11px] font-normal block">{itm?.email || itm.memberDetails?.email || '(Guest)'}</span>
                                    </div>
                                    {/* {deletePremit(itm) ? <>
                                    <Tooltip placement="top" title="Delete" className='cursor-pointer ml-auto text-red-500'> <span onClick={() => deleteItem(itm.id)} >
                                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/><path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/></svg>
                                    </span> </Tooltip>
                                  </> : <></>} */}
                                  </li>
                                </>
                              })}
                            </ul>
                          </div>

                          {addPremit() && invites.length ? <>
                            <div className="mt-6 mb-6">
                              <h6 className="text-[#C22020] text-[19px] leading-2  lg:leading-6	 font-[600]">Invitations ({invites.length})</h6>
                              <ul className="mt-3 ">
                                {invites.map((itm: any) => {
                                  return <>
                                   <li className="text-[#3F3F3F] text-[14px] flex border-b py-3 gap-3">
                                   <span><span>{itm?.memberDetails?.fullName || itm?.fullName}</span> ({itm?.email || itm.memberDetails?.email || 'Guest'})</span>

                                      {deletePremit(itm) ? <>
                                        <Tooltip placement="top" title="Accept" className='cursor-pointer ml-auto text-green-500 text-lg'> <span onClick={() => acceptReject(itm.id, 'accepted')} >
                                          <span className="material-symbols-outlined  text-base">done</span>
                                        </span> </Tooltip>
                                        <Tooltip placement="top" title="Reject" className='cursor-pointer text-red-500'> <span onClick={() => acceptReject(itm.id, 'rejected')} >
                                          <span className="material-symbols-outlined  text-base">close</span>
                                        </span> </Tooltip>
                                      </> : <></>}
                                    </li>
                                  </>
                                })}
                              </ul>
                            </div>
                          </> : <></>}

                        </>}
                        <div className="flex flex-col gap-2">
                            {(data?.meetingStatus !== "completed") && (
                              attendee.some((item: any) => matchesCurrentUser(item) && (item?.meetConfirm === 'Yes' || item?.isPresent === true)) ? (
                                <>
                                  <button
                                    className="flex items-center gap-1 border border-gray-300 py-2 px-2 rounded-xl hover:bg-orange-500 hover:text-white text-sm"
                                    onClick={() => setRequestGroup(true)}
                                  >
                                    <LiaUserClockSolid className="flex-shrink-0 text-lg" />
                                    <span className="text-center leading-tight">{connectMeetLabel} Request</span>
                                  </button>
                                  <button
                                    className="flex items-center gap-1 border border-gray-300 py-2 px-2 rounded-xl hover:bg-green-500 hover:text-white text-sm"
                                    onClick={() => setShowSubmitThankYou(true)}
                                  >
                                    <FaHandshake className="flex-shrink-0 text-lg" />
                                    <span className="text-center leading-tight">{testimonialLabel} Request</span>
                                  </button>
                                </>
                              ) : null
                            )}
                          {(data?.isMarkAttendance === true || meetingStart() || addPremit()) && (
                              data?.isGroupGenerated === true ? (
                                <button
                                  onClick={() => handleOpenModal("SeeAllGroups")}
                                  className="bg-[#46454E] flex items-center gap-1 py-2 px-2 text-white shadow hover:shadow-lg rounded-xl text-sm"
                                >
                                  <AiOutlineUsergroupAdd className="flex-shrink-0 text-lg" />
                                  <span className="text-center leading-tight">See All Groups</span>
                                </button>
                              ) : (
                                user?.customerRole?.permissions?.editGroups === true ? (
                                  <button
                                    onClick={() => handleOpenModal("GenerateConnectmeets")}
                                    className="bg-[#46454E] flex items-center gap-1 py-2 px-2 text-white shadow hover:shadow-lg rounded-xl text-sm"
                                  >
                                    {loading ? (
                                      <>
                                        <div className="loader_spin"></div>
                                        {loadingMessage}
                                      </>
                                    ) : (
                                      <>
                                        <AiOutlineUsergroupAdd className="flex-shrink-0 text-lg" />
                                        <span className="text-center leading-tight">Generate {connectMeetLabelPlural}</span>
                                      </>
                                    )}
                                  </button>
                                ) : null
                              )
                          )}
                        </div>

                      </div>

                    </div>




                  </div>




                </div>
              </div>
            </div>

          </div>
        </>}



      </PageLayout>

      {isModal ? <>
        <Modal
          title={`Invite ${isModal}`}
          result={e => {
            setModal('')
          }}
          onCloseConfirm={async () => {
            const dirtyRef = isModal === 'guest' ? guestFormDirtyRef : memberFormDirtyRef;
            if (!dirtyRef.current) return true;
            const result = await Swal.fire({
              title: "Save Event Invitation",
              text: "You have unsaved changes. Do you want to discard them?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              cancelButtonColor: "#6c757d",
              confirmButtonText: "Discard",
              cancelButtonText: "Continue Editing"
            });
            return result.isConfirmed;
          }}
          body={<>
            <AddAttendee
              eventDetail={data}
              eventId={id}
              guest={isModal == 'guest' ? true : false}
              result={e => {
                if (e.event == 'submit') {
                  setModal('')
                  getAttendee()
                  getInvites()
                }
              }}
              onDirtyChange={(dirty: boolean) => {
                if (isModal === 'guest') guestFormDirtyRef.current = dirty;
                else memberFormDirtyRef.current = dirty;
              }}
            />
          </>}
        />
      </> : <></>}

      {photoImportOpen ? <>
        <Modal
          title="Import Photo List"
          className="max-w-6xl"
          result={() => setPhotoImportOpen(false)}
          body={<>
            <p className="text-sm text-gray-600 mb-3">Review the entries below and click Send Invite on each side. Nothing is sent until you do. Close with the X to discard.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {photoMembers.length ? <div>
                <h4 className="font-semibold text-gray-800 mb-2">Invite Members ({photoMembers.length})</h4>
                <AddAttendee
                  eventDetail={data}
                  eventId={id}
                  guest={false}
                  initialMembers={photoMembers as any}
                  result={(e: any) => {
                    if (e.event == 'submit') {
                      getAttendee()
                      getInvites()
                      setPhotoMembers([])
                      if (!photoGuests.length) setPhotoImportOpen(false)
                    }
                  }}
                />
              </div> : null}
              {photoGuests.length ? <div>
                <h4 className="font-semibold text-gray-800 mb-2">Invite Guests ({photoGuests.length})</h4>
                <AddAttendee
                  eventDetail={data}
                  eventId={id}
                  guest={true}
                  initialMembers={photoGuests as any}
                  result={(e: any) => {
                    if (e.event == 'submit') {
                      getAttendee()
                      getInvites()
                      setPhotoGuests([])
                      if (!photoMembers.length) setPhotoImportOpen(false)
                    }
                  }}
                />
              </div> : null}
            </div>
          </>}
        />
      </> : <></>}

      {isRModal ? <>
        <Modal
          title={`Check-in List (${attendee.filter((a: any) => a.isPresent === true).length})`}


          body={<>
            <Members
              eventId={id}
              eventDetail={data}
              getDetailApi={getDetail}
            />
          </>}
          result={e => {
            setRModal(false)
          }}
        />
      </> : <></>}


      {/* {group ? <>
        <Modal
          
          title="Group List"
          body={<>
         <Groups
         eventDetail={attendeesGroup}
         dataa={data}
         />
          </>}
          result={e => {
            setGroup(false)
          }}
        />
      </> : <></>} */}
      {memberHistory ? <>
        <Modal

          title="Member History"
          body={<>
            <MemberHistory eventDetail={memberHistoryData} />
          </>}
          result={e => {
            setmemberHistory(false)
          }}
        />
      </> : <></>}

      {requestGroup ? <>
        <Modal

          title={`${connectMeetLabel} Request`}
          body={<>
            <RequestGroup eventDetail={attendeeFilter} data={data} setRequestGroup={setRequestGroup} connectMeetLabel={connectMeetLabel} />
          </>}
          result={e => {
            setRequestGroup(false)
          }}
        />
      </> : <></>}
      {isRsvpStatusModal ? <>
        <Modal
          title="RSVP Status"
          body={<>
            <div className="max-h-96 overflow-y-auto">
              <div className="mb-3 p-3 bg-gray-50 rounded">
                <div className="flex justify-between text-sm">
                  <span>Total Invited: <strong>{attendee.length}</strong></span>
                  <span className="text-green-600">RSVP Yes: <strong>{attendee.filter((a: any) => a.meetConfirm === 'Yes').length}</strong></span>
                  <span className="text-red-600">RSVP No: <strong>{attendee.filter((a: any) => a.meetConfirm === 'No').length}</strong></span>
                  <span className="text-yellow-600">Pending: <strong>{attendee.filter((a: any) => !a.meetConfirm || a.meetConfirm === 'Pending').length}</strong></span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-center p-2">RSVP</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[...attendee].sort((a: any, b: any) => {
                    const nameA = (a?.memberDetails?.fullName || a?.fullName || '').toLowerCase();
                    const nameB = (b?.memberDetails?.fullName || b?.fullName || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  }).map((itm: any, index: number) => (
                    <tr key={index} className="border-b">
                      <td className="p-2">{itm?.memberDetails?.fullName || itm?.fullName || 'Guest'}</td>
                      <td className="p-2">{itm?.email || itm?.memberDetails?.email}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          itm?.meetConfirm === 'Yes' ? 'bg-green-100 text-green-700' :
                          itm?.meetConfirm === 'No' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {itm?.meetConfirm || 'Pending'}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => {
                              loader(true);
                              ApiClient.put('api/attendees/update', { id: itm.id || itm._id, meetConfirm: 'Yes' }).then(res => {
                                loader(false);
                                if (res.success) {
                                  getAttendee();
                                }
                              });
                            }}
                            className={`px-2 py-1 rounded text-xs ${itm?.meetConfirm === 'Yes' ? 'bg-green-600 text-white' : 'bg-gray-200 hover:bg-green-500 hover:text-white'}`}
                          >
                            Yes
                          </button>
                          <button 
                            onClick={() => {
                              loader(true);
                              ApiClient.put('api/attendees/update', { id: itm.id || itm._id, meetConfirm: 'No' }).then(res => {
                                loader(false);
                                if (res.success) {
                                  getAttendee();
                                }
                              });
                            }}
                            className={`px-2 py-1 rounded text-xs ${itm?.meetConfirm === 'No' ? 'bg-red-600 text-white' : 'bg-gray-200 hover:bg-red-500 hover:text-white'}`}
                          >
                            No
                          </button>
                          <button 
                            onClick={() => {
                              loader(true);
                              ApiClient.put('api/attendees/update', { id: itm.id || itm._id, meetConfirm: 'Pending' }).then(res => {
                                loader(false);
                                if (res.success) {
                                  getAttendee();
                                }
                              });
                            }}
                            className={`px-2 py-1 rounded text-xs ${!itm?.meetConfirm || itm?.meetConfirm === 'Pending' ? 'bg-yellow-500 text-white' : 'bg-gray-200 hover:bg-yellow-400 hover:text-white'}`}
                          >
                            Pending
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendee.length === 0 && (
                <div className="text-center py-4 text-gray-500">No attendees yet</div>
              )}
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <div className="flex justify-between text-sm">
                  <span>Total Invited: <strong>{attendee.length}</strong></span>
                  <span className="text-green-600">RSVP Yes: <strong>{attendee.filter((a: any) => a.meetConfirm === 'Yes').length}</strong></span>
                  <span className="text-red-600">RSVP No: <strong>{attendee.filter((a: any) => a.meetConfirm === 'No').length}</strong></span>
                  <span className="text-yellow-600">Pending: <strong>{attendee.filter((a: any) => !a.meetConfirm || a.meetConfirm === 'Pending').length}</strong></span>
                </div>
              </div>
            </div>
          </>}
          result={e => {
            setRsvpStatusModal(false)
          }}
        />
      </> : <></>}
      {showManagerRequestModal ? <>
        <Modal
          title={`Manage ${connectMeetLabel} Requests`}
          body={<>
            <div className="max-h-96 overflow-y-auto">
              <p className="mb-4 text-sm text-gray-600">Select an attendee to make a {connectMeetLabel} request on their behalf:</p>
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-center p-2">Current Status</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                {attendee
                    .filter((itm: any) => itm.meetConfirm === 'Yes' || itm.isPresent)
                    .slice()
                    .sort((a: any, b: any) => {
                      const nameA = (a?.memberDetails?.fullName || a?.fullName || '').toLowerCase();
                      const nameB = (b?.memberDetails?.fullName || b?.fullName || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((itm: any, index: number) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="p-2">{itm?.memberDetails?.fullName || itm?.fullName || 'Guest'}</td>
                      <td className="p-2">{itm?.email || itm?.memberDetails?.email}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'connect_with_members' ? 'bg-blue-100 text-blue-700' :
                          getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'not_interested' ? 'bg-red-100 text-red-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'connect_with_members' ? 'Requested Specific' :
                          getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'not_interested' ? 'Not Available' :
                          'Available'}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <button 
                          onClick={() => {
                            setSelectedAttendeeForRequest(itm);
                          }}
                          className="px-3 py-1 rounded text-xs bg-[#EF7A2B] text-white hover:bg-orange-600"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendee.filter((itm: any) => itm.meetConfirm === 'Yes' || itm.isPresent).length === 0 && (
                <div className="text-center py-4 text-gray-500">No checked-in or RSVP'd attendees</div>
              )}
            </div>
          </>}
          result={e => {
            setShowManagerRequestModal(false);
            setSelectedAttendeeForRequest(null);
          }}
        />
      </> : <></>}

      {showSubmitThankYou && id ? <>
        <Modal
          title={`Submit ${testimonialLabel} Request`}
          body={<>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">Submit a {testimonialLabel.toLowerCase()} request. Your group facilitator will review it during the meeting.</p>
              <div>
                <label className="block text-sm font-medium mb-1">Who are you thanking?</label>
                <select
                  className="w-full border rounded-lg p-2 text-sm"
                  value={tyReceiverId}
                  onChange={async (e) => {
                    const rid = e.target.value;
                    setTyReceiverId(rid);
                    setTySharedGroups([]);
                    setTySelectedGroupIds(data?.groupId?._id ? [data.groupId._id] : []);
                    if (rid) {
                      const res = await ApiClient.get('api/testimonial/shared-groups', { receiverId: rid });
                      if (res.success && res.data) {
                        setTySharedGroups(res.data);
                        setTySelectedGroupIds(data?.groupId?._id ? [data.groupId._id] : []);
                      }
                    }
                  }}
                >
                  <option value="">Select member...</option>
                  {attendee
                    .filter((a: any) => {
                      const memberId = a.memberDetails?._id || a.userId;
                      return memberId && memberId !== user?._id && (a.meetConfirm === 'Yes' || a.isPresent);
                    })
                    .sort((a: any, b: any) => {
                      const nameA = (a.memberDetails?.fullName || a.fullName || '').toLowerCase();
                      const nameB = (b.memberDetails?.fullName || b.fullName || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((a: any) => (
                      <option key={a.memberDetails?._id || a.userId} value={a.memberDetails?._id || a.userId}>
                        {a.memberDetails?.fullName || a.fullName}{a.type === 'guest' ? ' (guest)' : ''}
                      </option>
                    ))}
                </select>
              </div>
              {tySharedGroups.length > 1 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Also submit to shared groups (optional)</label>
                  <div className="space-y-1">
                    {tySharedGroups.map((g: any) => {
                      const isCurrentGroup = g._id === data?.groupId?._id;
                      const isChecked = tySelectedGroupIds.includes(g._id);
                      return (
                        <label key={g._id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isCurrentGroup}
                            onChange={() => {
                              if (isCurrentGroup) return;
                              setTySelectedGroupIds(prev =>
                                prev.includes(g._id) ? prev.filter(id => id !== g._id) : [...prev, g._id]
                              );
                            }}
                          />
                          <span>{g.name}{isCurrentGroup ? ' (this event)' : ''}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Type (optional)</label>
                <select className="w-full border rounded-lg p-2 text-sm" value={tyType} onChange={(e) => setTyType(e.target.value)}>
                  <option value="">Select type...</option>
                  <option value="referral">Referral</option>
                  <option value="introduction">Introduction</option>
                  <option value="business_outcome">Business Outcome</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note (optional)</label>
                <textarea
                  className="w-full border rounded-lg p-2 text-sm"
                  value={tyNote}
                  onChange={(e) => setTyNote(e.target.value)}
                  placeholder="Brief description..."
                  maxLength={500}
                  rows={2}
                />
              </div>
              <button
                disabled={!tyReceiverId || tySubmitting}
                className="bg-[#22C55E] text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-green-600 text-sm"
                onClick={async () => {
                  setTySubmitting(true);
                  const groupIdsToSend = tySelectedGroupIds.length > 0 ? tySelectedGroupIds : [data?.groupId?._id];
                  const res = await ApiClient.post('api/testimonial/submit', {
                    receivedById: tyReceiverId,
                    groupId: data?.groupId?._id,
                    groupIds: groupIdsToSend,
                    eventId: id,
                    thankYouType: tyType || null,
                    note: tyNote || null
                  });
                  setTySubmitting(false);
                  if (res.success) {
                    toast.success(`${testimonialLabel} request submitted successfully`);
                    setTyReceiverId('');
                    setTyType('');
                    setTyNote('');
                    setTySharedGroups([]);
                    setTySelectedGroupIds([]);
                    setShowSubmitThankYou(false);
                  }
                }}
              >
                {tySubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </>}
          result={e => {
            setShowSubmitThankYou(false);
          }}
        />
      </> : <></>}
      {showThankYouModal && id ? <>
        <Modal
          title={`Manage ${testimonialLabelPlural}`}
          body={<>
            <ThankYouManager
              eventId={id}
              groupId={data?.groupId?._id || ''}
              attendees={attendee}
              user={user}
              label={testimonialLabelPlural}
              onClose={() => setShowThankYouModal(false)}
            />
          </>}
          result={e => {
            setShowThankYouModal(false);
          }}
        />
      </> : <></>}
      {selectedAttendeeForRequest ? <>
        <Modal
          title={`${connectMeetLabel} Request for ${selectedAttendeeForRequest?.memberDetails?.fullName || selectedAttendeeForRequest?.fullName}`}
          body={<>
            <ManagerRequestForm 
              attendee={selectedAttendeeForRequest}
              allAttendees={attendee}
              eventId={id}
              groupId={data?.groupId?._id}
              user={user}
              onComplete={() => {
                setSelectedAttendeeForRequest(null);
                getAttendee();
                // Refresh the request statuses
                ApiClient.get("api/group-request/list", { eventId: id }).then(res => {
                  if (res?.success && res?.data) {
                    const statusMap: {[email: string]: string} = {};
                    res.data.forEach((req: any) => {
                      if (req.requestedBy) {
                        statusMap[req.requestedBy] = req.requestType || 'system_generate';
                      }
                    });
                    setAttendeeRequestStatuses(statusMap);
                  }
                });
              }}
              connectMeetLabel={connectMeetLabel}
            />
          </>}
          result={e => {
            setSelectedAttendeeForRequest(null);
          }}
        />
      </> : <></>}
    </>
  );
};

export default EventDetail;
